import { useMemo, useState } from "react";
import type { TimeDay } from "../api/types";
import { useAutoAttach } from "../hooks/useAutoAttach";
import { useUnassignedTime } from "../hooks/useUnassignedTime";
import { AUTO_LABELS, NEGLIGIBLE_DAY_MIN } from "../lib/autoAttach";
import {
  friseClues,
  friseGroups,
  lastSegment,
  type FriseClues as Clues,
  type FriseGroup,
} from "../lib/dayFrise";
import { formatMinutes } from "../lib/format";
import {
  activityColor,
  boardKey,
  buildWeekBoard,
  type BoardBlock,
  type BoardDayActivity,
} from "../lib/weekBoard";
import { FriseClues } from "./FriseClues";
import { ReimputeSheet, type ReimputeTarget } from "./ReimputeSheet";
import {
  DOW,
  FriseAxisRow,
  FriseSubrow,
  FriseTooltip,
  dur,
  friseAxis,
  hm,
  useFollowTip,
} from "./TimelineWeekBoard";
import {
  AttachSourceSheet,
  useConfirmSuggestion,
  type AttachTarget,
} from "./UnassignedSources";

interface Pick {
  target: AttachTarget;
  eventIds: string[];
  retain: boolean;
  createName?: string;
  clues: Clues;
}

function pickOf(
  group: FriseGroup | undefined,
  eventIds: string[],
  minutes: number,
  retain: boolean,
  fallbackTitle: string,
  clues: Clues,
): Pick {
  if (!group) {
    return {
      target: { kind: "none", value: "", minutes, title: fallbackTitle },
      eventIds,
      retain: false,
      clues,
    };
  }
  const named = group.kind === "root" || group.kind === "code";
  return {
    target: {
      kind:
        group.kind === "domain"
          ? "domain"
          : group.kind === "code"
            ? "code"
            : "none",
      value: group.value,
      ...(group.branch ? { branch: group.branch } : {}),
      minutes,
      ...(group.field ? { field: group.field } : {}),
      title: group.label,
    },
    eventIds,
    retain,
    clues,
    ...(named ? { createName: lastSegment(group.value) } : {}),
  };
}

/** Un indice de temps sans projet : le projet suggéré se confirme d'un clic,
 * un autre se choisit, et l'indice se retient en règle. */
function GroupRow({
  group,
  clues,
  workerUrl,
  editable,
  onHover,
  onPick,
}: {
  group: FriseGroup;
  clues: Clues;
  workerUrl: string;
  editable: boolean;
  onHover: (key: string | null) => void;
  onPick: (group: FriseGroup, retain: boolean) => void;
}) {
  const [retain, setRetain] = useState(false);
  const { confirm, pending, error } = useConfirmSuggestion(workerUrl);
  const sp = group.suggestion;
  const spLabel = sp?.project_label || "un projet déjà utilisé";
  const count = group.eventIds.length;
  const name = group.branch ? `${group.label} · ${group.branch}` : group.label;

  return (
    <li
      className="wb-group"
      onMouseEnter={() => onHover(group.key)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(group.key)}
      onBlur={() => onHover(null)}
    >
      <span className="wb-gname">{name}</span>
      <span className="wb-gdur">
        {formatMinutes(group.minutes)} · {count} créneau{count > 1 ? "x" : ""}
      </span>
      {sp && (
        <span className="wb-gsugg" title={`Parce que ${sp.reason}`}>
          {spLabel} ?
        </span>
      )}
      {editable && (
        <span className="wb-gact">
          {sp && (
            <button
              type="button"
              className="pill-btn tap"
              aria-label={`Confirmer ${spLabel} pour ${name}`}
              title={`Parce que ${sp.reason}`}
              disabled={pending}
              onClick={() =>
                void confirm({
                  suggestion: sp,
                  eventIds: group.eventIds,
                  minutes: group.minutes,
                  ...(retain && group.field
                    ? {
                        retain: {
                          field: group.field,
                          value: group.value,
                          ...(group.branch ? { branch: group.branch } : {}),
                        },
                      }
                    : {}),
                })
              }
            >
              {pending ? "…" : "✓"}
            </button>
          )}
          <button
            type="button"
            className="pill-btn secondary tap"
            aria-label={`Choisir un projet pour ${name}`}
            onClick={() => onPick(group, retain)}
          >
            {sp ? "▾" : "Projet ▾"}
          </button>
          {group.field && (
            <label className="wb-retain">
              <input
                type="checkbox"
                checked={retain}
                onChange={(e) => setRetain(e.target.checked)}
                aria-label={`Retenir ${name}`}
              />
              retenir
            </label>
          )}
        </span>
      )}
      <FriseClues
        clues={clues}
        {...(group.kind === "title" ? { skipTitle: group.value } : {})}
      />
      {error && (
        <p className="reimpute-error" role="alert">
          {error}
        </p>
      )}
    </li>
  );
}

/**
 * Une journée sur l'axe horaire de la board de semaine : la ligne du jour,
 * une ligne par projet, puis le temps sans projet, hachuré et toujours en
 * dernier. Sous lui, ses créneaux rangés par indice se rattachent sur place.
 */
export function DayFrise({
  workerUrl,
  day,
  timeDay,
  firstMin,
  lastMin,
  workedMin,
  unassignedMin,
  editable,
}: {
  workerUrl: string;
  day: string;
  timeDay: TimeDay | undefined;
  firstMin: number | null;
  lastMin: number | null;
  workedMin: number;
  unassignedMin: number;
  editable: boolean;
}) {
  const board = useMemo(
    () =>
      buildWeekBoard(
        day,
        day,
        new Map(timeDay ? [[day, timeDay] as const] : []),
      ),
    [day, timeDay],
  );
  const bd = board.days[0];
  const { pct, width, gridlines } = friseAxis(board);
  const { tip, follow } = useFollowTip();
  const [open, setOpen] = useState(true);
  const [hovered, setHovered] = useState<string | null>(null);
  const [pick, setPick] = useState<Pick | null>(null);
  const [change, setChange] = useState<ReimputeTarget[] | null>(null);
  const unassigned = useUnassignedTime(workerUrl, day, day, {
    enabled: unassignedMin > 0,
  });
  useAutoAttach(
    unassigned.data?.activities,
    timeDay ? [timeDay] : [],
    editable,
  );
  const claimMin = unassignedMin < NEGLIGIBLE_DAY_MIN ? 0 : unassignedMin;

  const autoByEvent = new Map(
    (timeDay?.blocks ?? []).flatMap((b) =>
      b.start_event_id && b.auto_imputation
        ? [[b.start_event_id, b.auto_imputation] as const]
        : [],
    ),
  );
  const activities = unassigned.data?.activities ?? [];
  const cluesOf = (eventIds: string[]) =>
    friseClues(activities, eventIds, bd?.blocks ?? []);
  const groups = friseGroups(activities);
  const groupByEvent = new Map<string, FriseGroup>();
  for (const g of groups) for (const id of g.eventIds) groupByEvent.set(id, g);

  const unassignedKeys = new Set(
    (timeDay?.project_lines ?? [])
      .filter((l) => !l.project_id)
      .map((l) => boardKey(l)),
  );
  const isUnassigned = (a: BoardDayActivity) =>
    a.blocks.length > 0
      ? a.blocks.every((b) => !b.assigned)
      : unassignedKeys.has(a.code);
  const projects = (bd?.activities ?? []).filter((a) => !isUnassigned(a));
  const loose = (bd?.blocks ?? []).filter((b) => !b.assigned);
  const nameOf = (code: string) => code || "Présence hors activité";
  const assignedMin = Math.max(workedMin - unassignedMin, 0);
  const progress = workedMin > 0 ? (assignedMin / workedMin) * 100 : 100;
  const d = new Date(`${day}T12:00:00Z`);

  const blockStyle = (b: BoardBlock, color?: string) => ({
    left: `${pct(b.startMin)}%`,
    width: `${width(b.startMin, b.endMin)}%`,
    ...(color ? { background: color } : {}),
  });
  const range = (b: BoardBlock) => `${hm(b.startMin)} à ${hm(b.endMin)}`;

  const pickBlock = (b: BoardBlock) => {
    if (!b.eventId) return;
    const g = groupByEvent.get(b.eventId);
    setPick(
      pickOf(
        g,
        [b.eventId],
        b.endMin - b.startMin,
        false,
        `Créneau ${range(b)}`,
        cluesOf([b.eventId]),
      ),
    );
  };

  const hatched = (b: BoardBlock, i: number, core: boolean) => {
    const g = b.eventId ? groupByEvent.get(b.eventId) : undefined;
    const cls = `wb-seg wb-hatched${core ? " wb-blk" : ""}${
      hovered && g?.key === hovered ? " wb-hl" : ""
    }`;
    const tipContent = (
      <>
        <b>Sans projet</b>
        {g && ` · ${g.label}`}
        <br />
        {hm(b.startMin)} → {hm(b.endMin)} · {dur(b.endMin - b.startMin)}
      </>
    );
    return core && editable && b.eventId ? (
      <button
        key={`u-${i}`}
        type="button"
        className={cls}
        style={blockStyle(b)}
        {...follow(tipContent)}
        onClick={(e) => {
          e.stopPropagation();
          pickBlock(b);
        }}
        aria-label={`Rattacher le créneau ${range(b)}`}
      />
    ) : (
      <div
        key={`u-${i}`}
        className={cls}
        style={blockStyle(b)}
        {...follow(tipContent)}
      />
    );
  };

  const toggle = () => setOpen(!open);

  return (
    <div className="wb-root wb-day">
      <div className="wb-board">
        <div className="wb-board-inner">
          <FriseAxisRow ticks={board.ticks} pct={pct} />
          <div
            className="wb-row wb-expandable"
            role="button"
            tabIndex={0}
            aria-expanded={open}
            aria-label="Frise de la journée"
            onClick={toggle}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggle();
              }
            }}
          >
            <div className="wb-daylabel">
              <div className="wb-name">
                <span className="wb-chev">▶</span>
                {DOW[d.getUTCDay()]} {d.getUTCDate()}
              </div>
              <div className="wb-range">
                {firstMin !== null && lastMin !== null && (
                  <>
                    {hm(firstMin)} → {hm(lastMin)} ·{" "}
                  </>
                )}
                <span className="wb-total">{formatMinutes(workedMin)}</span>
              </div>
              {claimMin > 0 && (
                <div className="wb-rest">
                  reste {formatMinutes(unassignedMin)} à rattacher
                </div>
              )}
              <div
                className="wb-progress"
                role="progressbar"
                aria-label="Temps rattaché à un projet"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress)}
              >
                <span style={{ width: `${progress}%` }} />
              </div>
            </div>
            <div className="wb-track">
              {gridlines}
              <div className="wb-lane-work">
                {(bd?.blocks ?? []).map((b, i) =>
                  b.assigned ? (
                    <div
                      key={`a-${i}`}
                      className="wb-seg"
                      style={blockStyle(b, activityColor(b.code))}
                      {...follow(
                        <>
                          <b>{nameOf(b.code)}</b>
                          <br />
                          {hm(b.startMin)} → {hm(b.endMin)} ·{" "}
                          {dur(b.endMin - b.startMin)}
                        </>,
                      )}
                    />
                  ) : (
                    hatched(b, i, false)
                  ),
                )}
              </div>
            </div>
          </div>

          {open && (
            <div className="wb-subrows">
              {projects.map((a) => (
                <FriseSubrow
                  key={a.code}
                  name={nameOf(a.code)}
                  color={activityColor(a.code)}
                  meta={formatMinutes(a.minutes)}
                  gridlines={gridlines}
                >
                  {a.blocks.map((b, i) => {
                    const auto = b.eventId
                      ? autoByEvent.get(b.eventId)
                      : undefined;
                    if (auto) {
                      const why = `Rattaché automatiquement (${AUTO_LABELS[auto]})`;
                      const tipAuto = follow(
                        <>
                          <b>{nameOf(a.code)}</b>
                          <br />
                          {hm(b.startMin)} → {hm(b.endMin)} ·{" "}
                          {dur(b.endMin - b.startMin)}
                          <br />
                          {why}
                        </>,
                      );
                      const mark = (
                        <span className="wb-automark" aria-hidden="true">
                          auto
                        </span>
                      );
                      return editable ? (
                        <button
                          key={`${a.code}-${i}`}
                          type="button"
                          className="wb-seg wb-blk wb-auto"
                          style={blockStyle(b, activityColor(a.code))}
                          {...tipAuto}
                          onClick={() =>
                            setPick({
                              target: {
                                kind: "none",
                                value: "",
                                minutes: b.endMin - b.startMin,
                                title: `${nameOf(a.code)}, ${range(b)}`,
                              },
                              eventIds: [b.eventId!],
                              retain: false,
                              clues: cluesOf([b.eventId!]),
                            })
                          }
                          aria-label={`${why} ${range(b)}, changer de projet`}
                        >
                          {mark}
                        </button>
                      ) : (
                        <div
                          key={`${a.code}-${i}`}
                          className="wb-seg wb-blk wb-auto"
                          style={blockStyle(b, activityColor(a.code))}
                          {...tipAuto}
                          aria-label={`${why} ${range(b)}`}
                        >
                          {mark}
                        </div>
                      );
                    }
                    return editable && b.eventId ? (
                      <button
                        key={`${a.code}-${i}`}
                        type="button"
                        className="wb-seg wb-blk"
                        style={blockStyle(b, activityColor(a.code))}
                        {...follow(
                          <>
                            <b>{nameOf(a.code)}</b>
                            <br />
                            {hm(b.startMin)} → {hm(b.endMin)} ·{" "}
                            {dur(b.endMin - b.startMin)}
                          </>,
                        )}
                        onClick={() => {
                          const projectId = timeDay?.blocks.find(
                            (x) => x.start_event_id === b.eventId,
                          )?.project_id;
                          setChange([
                            {
                              eventId: b.eventId!,
                              label: `${nameOf(a.code)}, ${range(b)}`,
                              ...(projectId ? { projectId } : {}),
                            },
                          ]);
                        }}
                        aria-label={`Changer de projet ${range(b)}`}
                      />
                    ) : (
                      <div
                        key={`${a.code}-${i}`}
                        className="wb-seg wb-blk"
                        style={blockStyle(b, activityColor(a.code))}
                      />
                    );
                  })}
                </FriseSubrow>
              ))}
              {unassignedMin > 0 && (
                <FriseSubrow
                  className="wb-loose"
                  name="Sans projet"
                  meta={formatMinutes(unassignedMin)}
                  gridlines={gridlines}
                >
                  {loose.map((b, i) => hatched(b, i, true))}
                </FriseSubrow>
              )}
            </div>
          )}

          {claimMin > 0 &&
            (unassigned.isError ? (
              <p className="wb-gnote">Temps sans projet indisponible</p>
            ) : unassigned.isLoading ? (
              <p className="wb-gnote">Chargement…</p>
            ) : (
              <>
                {!editable && (
                  <p className="wb-gnote">
                    La feuille est déjà soumise : ce temps reste sans projet.
                  </p>
                )}
                <ul
                  className="wb-groups"
                  aria-label="Temps sans projet par indice"
                >
                  {groups.map((g) => (
                    <GroupRow
                      key={g.key}
                      group={g}
                      clues={cluesOf(g.eventIds)}
                      workerUrl={workerUrl}
                      editable={editable}
                      onHover={setHovered}
                      onPick={(group, retain) =>
                        setPick(
                          pickOf(
                            group,
                            group.eventIds,
                            group.minutes,
                            retain,
                            group.label,
                            cluesOf(group.eventIds),
                          ),
                        )
                      }
                    />
                  ))}
                </ul>
              </>
            ))}
        </div>
      </div>
      <FriseTooltip tip={tip} />
      {pick && (
        <AttachSourceSheet
          source={pick.target}
          workerUrl={workerUrl}
          eventIds={pick.eventIds}
          retain={pick.retain}
          {...(pick.createName ? { createName: pick.createName } : {})}
          details={<FriseClues clues={pick.clues} />}
          onClose={() => setPick(null)}
        />
      )}
      {change && (
        <ReimputeSheet targets={change} onClose={() => setChange(null)} />
      )}
    </div>
  );
}
